import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Printer, Tag, Settings2, Plus, Trash2, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  getPrinterIp, setPrinterIp, sendZpl,
  skuLabelZpl, snLabelZpl,
} from '@/lib/zebraPrinter';
import {
  printHtmlLabels,
  skuLabelHtml62mm, snLabelHtml62mm,
} from '@/lib/qzPrinter';

// ── Types ────────────────────────────────────────────────────────────────────

interface LabelJob {
  id: string;
  type: 'sku' | 'sn';
  value: string;        // SKU code or serial number
  description?: string; // only for SKU labels
  copies: number;
  selected: boolean;
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function PrintLabelsPage() {
  // ── Printer assignment from DB ───────────────────────────────────────
  const [printerConfig, setPrinterConfig] = useState<{ label_printer_type: string; label_printer_name: string | null; label_printer_ip: string | null } | null>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: tech } = await supabase
        .from('technicians')
        .select('id')
        .eq('user_id', user.id)
        .single();
      if (!tech) return;
      const { data: assignment } = await supabase
        .from('printer_assignments')
        .select('label_printer_type, label_printer_name, label_printer_ip')
        .eq('technician_id', tech.id)
        .single();
      if (assignment) setPrinterConfig(assignment);
    })();
  }, []);

  // ── Printer config ─────────────────────────────────────────────────────
  const [configOpen, setConfigOpen] = useState(false);
  const [ip, setIp] = useState(getPrinterIp);

  const saveIp = () => {
    setPrinterIp(ip);
    setConfigOpen(false);
    toast.success('Printer IP saved');
  };

  // ── Label queue ────────────────────────────────────────────────────────
  const [jobs, setJobs] = useState<LabelJob[]>([]);

  const addJob = (type: 'sku' | 'sn', value: string, description?: string) => {
    setJobs(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type,
        value,
        description,
        copies: 3,
        selected: true,
      },
    ]);
  };

  const removeJob = (id: string) => setJobs(prev => prev.filter(j => j.id !== id));

  const updateJob = (id: string, patch: Partial<LabelJob>) =>
    setJobs(prev => prev.map(j => j.id === id ? { ...j, ...patch } : j));

  const selectAll = (sel: boolean) => setJobs(prev => prev.map(j => ({ ...j, selected: sel })));

  // ── Manual add ─────────────────────────────────────────────────────────
  const [manualType, setManualType] = useState<'sku' | 'sn'>('sku');
  const [manualValue, setManualValue] = useState('');
  const [manualDesc, setManualDesc] = useState('');

  const addManual = () => {
    if (!manualValue.trim()) return;
    addJob(manualType, manualValue.trim(), manualType === 'sku' ? manualDesc.trim() : undefined);
    setManualValue('');
    setManualDesc('');
  };

  // ── Load from assembly order ───────────────────────────────────────────
  const [aoSearch, setAoSearch] = useState('');
  const [aoSearchOpen, setAoSearchOpen] = useState(false);

  const { data: aoResults = [], isFetching: aoFetching } = useQuery({
    queryKey: ['label-ao-search', aoSearch],
    queryFn: async () => {
      if (aoSearch.length < 2) return [];
      let query = supabase
        .from('assembly_orders')
        .select('id, order_number, build_sku, quantity, serial_number, serial_numbers, build:builds(description)')
        .order('created_at', { ascending: false })
        .limit(20);

      // Search by order number, build SKU, or serial number
      query = query.or(`order_number.ilike.%${aoSearch}%,build_sku.ilike.%${aoSearch}%,serial_number.ilike.%${aoSearch}%`);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: aoSearch.length >= 2 && aoSearchOpen,
  });

  const loadFromOrder = (order: any) => {
    const desc = (order.build as any)?.description || '';
    // Add SKU label
    addJob('sku', order.build_sku, desc);

    // Add S/N labels
    const qty = Math.max(order.quantity || 1, 1);
    const sns: Record<string, string> = order.serial_numbers || {};
    for (let i = 1; i <= qty; i++) {
      const sn = sns[String(i)] || (i === 1 ? order.serial_number : '') || '';
      if (sn) {
        addJob('sn', sn);
      }
    }

    setAoSearchOpen(false);
    setAoSearch('');
    toast.success(`Loaded labels from ${order.order_number}`);
  };

  // ── Load from builds list ──────────────────────────────────────────────
  const [buildSearch, setBuildSearch] = useState('');
  const [buildSearchOpen, setBuildSearchOpen] = useState(false);

  const { data: buildResults = [], isFetching: buildFetching } = useQuery({
    queryKey: ['label-build-search', buildSearch],
    queryFn: async () => {
      if (buildSearch.length < 2) return [];
      const { data, error } = await supabase
        .from('builds')
        .select('id, sku, description')
        .or(`sku.ilike.%${buildSearch}%,description.ilike.%${buildSearch}%`)
        .order('sku')
        .limit(20);
      if (error) throw error;
      return data ?? [];
    },
    enabled: buildSearch.length >= 2 && buildSearchOpen,
  });

  const loadFromBuild = (build: any) => {
    addJob('sku', build.sku, build.description || '');
    setBuildSearchOpen(false);
    setBuildSearch('');
  };

  // ── Preview ────────────────────────────────────────────────────────────
  const [previewJob, setPreviewJob] = useState<LabelJob | null>(null);

  // ── Print ──────────────────────────────────────────────────────────────
  const [printing, setPrinting] = useState(false);
  const [progress, setProgress] = useState({ sent: 0, total: 0, failed: 0 });

  const selectedJobs = useMemo(() => jobs.filter(j => j.selected), [jobs]);
  const allSelected = jobs.length > 0 && jobs.every(j => j.selected);
  const noneSelected = jobs.every(j => !j.selected);
  const progressPct = progress.total > 0 ? Math.round((progress.sent / progress.total) * 100) : 0;

  const doPrint = async () => {
    const printerType = printerConfig?.label_printer_type || 'zebra';
    const printerName = printerConfig?.label_printer_name;
    const printerIp = printerConfig?.label_printer_ip || getPrinterIp();
    const useBrother = printerType === 'brother_62mm';

    if (useBrother && !printerName) {
      toast.error('No Brother printer configured — ask your admin to assign one');
      return;
    }
    if (!useBrother && !printerIp) {
      toast.error('Set the Zebra printer IP first');
      setConfigOpen(true);
      return;
    }
    if (selectedJobs.length === 0) {
      toast.error('No labels selected');
      return;
    }

    setPrinting(true);

    try {
      if (useBrother) {
        // ── Brother 62mm via QZ Tray ──
        const htmlLabels: string[] = [];
        for (const job of selectedJobs) {
          for (let c = 0; c < job.copies; c++) {
            if (job.type === 'sku') {
              htmlLabels.push(...skuLabelHtml62mm(job.value, job.description || ''));
            } else {
              htmlLabels.push(snLabelHtml62mm(job.value));
            }
          }
        }
        await printHtmlLabels(printerName!, htmlLabels);
        toast.success(`${htmlLabels.length} label${htmlLabels.length > 1 ? 's' : ''} printed`);
      } else {
        // ── Zebra: direct HTTP to IP (existing flow) ──
        const total = selectedJobs.length;
        setProgress({ sent: 0, total, failed: 0 });
        let sent = 0;
        let failed = 0;

        for (const job of selectedJobs) {
          const zpl = job.type === 'sku'
            ? skuLabelZpl(job.value, job.description || '', job.copies)
            : snLabelZpl(job.value, job.copies);

          const result = await sendZpl(zpl, printerIp);
          if (result.ok) sent++; else failed++;
          setProgress({ sent: sent + failed, total, failed });
        }

        if (failed === 0) {
          toast.success(`All ${sent} label job${sent > 1 ? 's' : ''} sent to printer`);
        } else {
          toast.error(`${failed} of ${total} label jobs failed`);
        }
      }
    } catch (e: any) {
      const msg = e.message || 'Print failed';
      if (msg.includes('Unable to connect')) {
        toast.error('QZ Tray not running — install from qz.io and start it');
      } else {
        toast.error(msg);
      }
    } finally {
      setPrinting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Print Labels</h1>
          <p className="text-sm text-muted-foreground">Zebra ZT220 label printing</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {getPrinterIp() || 'No printer configured'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => { setIp(getPrinterIp()); setConfigOpen(true); }}>
            <Settings2 className="h-4 w-4 mr-1" /> Printer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left: Add labels ──────────────────────────────────────────── */}
        <div className="space-y-4">
          {/* Load from Assembly Order */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Load from Assembly Order</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search order #, SKU, or S/N…"
                  value={aoSearch}
                  onChange={e => { setAoSearch(e.target.value); setAoSearchOpen(true); }}
                  onFocus={() => setAoSearchOpen(true)}
                  className="text-sm"
                />
              </div>
              {aoSearchOpen && aoSearch.length >= 2 && (
                <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                  {aoFetching && <p className="text-xs text-muted-foreground p-2">Searching…</p>}
                  {!aoFetching && aoResults.length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">No results</p>
                  )}
                  {aoResults.map((o: any) => (
                    <button
                      key={o.id}
                      className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                      onClick={() => loadFromOrder(o)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-xs font-medium">{o.order_number}</span>
                        <span className="text-xs text-muted-foreground">{o.quantity || 1} unit{(o.quantity || 1) > 1 ? 's' : ''}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{o.build_sku}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Load from Build */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Load from Build</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Input
                placeholder="Search build SKU or description…"
                value={buildSearch}
                onChange={e => { setBuildSearch(e.target.value); setBuildSearchOpen(true); }}
                onFocus={() => setBuildSearchOpen(true)}
                className="text-sm"
              />
              {buildSearchOpen && buildSearch.length >= 2 && (
                <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
                  {buildFetching && <p className="text-xs text-muted-foreground p-2">Searching…</p>}
                  {!buildFetching && buildResults.length === 0 && (
                    <p className="text-xs text-muted-foreground p-2">No results</p>
                  )}
                  {buildResults.map((b: any) => (
                    <button
                      key={b.id}
                      className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
                      onClick={() => loadFromBuild(b)}
                    >
                      <p className="text-sm font-medium">{b.sku}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.description || '—'}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual add */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Add Manual Label</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2">
                <select
                  value={manualType}
                  onChange={e => setManualType(e.target.value as 'sku' | 'sn')}
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                >
                  <option value="sku">SKU</option>
                  <option value="sn">S/N</option>
                </select>
                <Input
                  placeholder={manualType === 'sku' ? 'SKU code' : 'Serial number'}
                  value={manualValue}
                  onChange={e => setManualValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addManual()}
                  className="text-sm font-mono"
                />
              </div>
              {manualType === 'sku' && (
                <Input
                  placeholder="Description (optional)"
                  value={manualDesc}
                  onChange={e => setManualDesc(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addManual()}
                  className="text-sm"
                />
              )}
              <Button variant="outline" size="sm" className="w-full" onClick={addManual} disabled={!manualValue.trim()}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Add to Queue
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* ── Right: Label queue + print ────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Label Queue ({jobs.length})</CardTitle>
                <div className="flex items-center gap-2">
                  {jobs.length > 0 && (
                    <>
                      <button
                        className="text-xs text-primary hover:underline"
                        onClick={() => selectAll(!allSelected)}
                      >
                        {allSelected ? 'Deselect all' : 'Select all'}
                      </button>
                      <button
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => setJobs([])}
                      >
                        Clear all
                      </button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Tag className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No labels in queue</p>
                  <p className="text-xs">Load from an assembly order, build, or add manually</p>
                </div>
              ) : (
                <div className="border rounded-md divide-y">
                  {jobs.map(job => (
                    <div key={job.id} className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={job.selected}
                        onChange={() => updateJob(job.id, { selected: !job.selected })}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      <Badge variant={job.type === 'sku' ? 'default' : 'secondary'} className="text-[10px] w-8 justify-center shrink-0">
                        {job.type === 'sku' ? 'SKU' : 'S/N'}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono truncate">{job.value}</p>
                        {job.type === 'sku' && job.description && (
                          <p className="text-xs text-muted-foreground truncate">{job.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-xs text-muted-foreground">×</span>
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          value={job.copies}
                          onChange={e => updateJob(job.id, { copies: parseInt(e.target.value) || 1 })}
                          className="h-7 w-14 text-center text-sm px-1"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        title="Preview"
                        onClick={() => setPreviewJob(job)}
                      >
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700 shrink-0"
                        onClick={() => removeJob(job.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Progress bar */}
              {progress.total > 0 && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Sending to printer…</span>
                    <span>
                      {progress.sent} / {progress.total}
                      {progress.failed > 0 && (
                        <span className="text-red-600 ml-1">({progress.failed} failed)</span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        progress.failed > 0 ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Print button */}
              {jobs.length > 0 && (
                <div className="mt-4 flex justify-end">
                  <Button
                    size="lg"
                    disabled={printing || noneSelected}
                    onClick={doPrint}
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {printing
                      ? 'Printing…'
                      : `Print ${selectedJobs.length} Label${selectedJobs.length !== 1 ? 's' : ''}`}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Preview Dialog ──────────────────────────────────────────────── */}
      <Dialog open={!!previewJob} onOpenChange={() => setPreviewJob(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Label Preview</DialogTitle>
            <DialogDescription>
              {previewJob?.type === 'sku' ? 'SKU' : 'Serial Number'} label — {previewJob?.copies} {previewJob?.copies === 1 ? 'copy' : 'copies'}
            </DialogDescription>
          </DialogHeader>
          {previewJob && <LabelPreview job={previewJob} />}
        </DialogContent>
      </Dialog>

      {/* ── Printer config dialog ───────────────────────────────────────── */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>Zebra Printer</DialogTitle>
            <DialogDescription>Enter the printer's IP address on your network.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="e.g. 192.168.1.100"
              value={ip}
              onChange={e => setIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveIp()}
              className="font-mono"
            />
            <Button className="w-full" onClick={saveIp}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Label Preview Component ──────────────────────────────────────────────────
// Renders a visual approximation of the 3" × 1⅝" label using HTML/CSS

function LabelPreview({ job }: { job: LabelJob }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 2× resolution for sharpness (3" × 1⅝" aspect ratio)
    const W = 600;
    const H = 325;
    canvas.width = W;
    canvas.height = H;

    // White background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, W, H);

    ctx.fillStyle = '#000';
    ctx.textBaseline = 'top';

    // Margins match ZPL: 60 left, barcode centered
    const L = 60;
    const barcodeTop = 125;
    const barcodeH = 130;
    const humanY = barcodeTop + barcodeH + 6;

    if (job.type === 'sku') {
      // SKU — large
      ctx.font = 'bold 48px monospace';
      ctx.fillText(job.value, L, 22);

      // Description — wider allowance
      if (job.description) {
        ctx.font = '24px sans-serif';
        const desc = job.description.length > 44 ? job.description.slice(0, 41) + '…' : job.description;
        ctx.fillText(desc, L, 78);
      }

      // Barcode — centered
      drawBarcode(ctx, job.value, 40, barcodeTop, W - 80, barcodeH);

      // Human-readable — centered
      ctx.font = '18px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(job.value, W / 2, humanY);
      ctx.textAlign = 'left';
    } else {
      // Description header
      ctx.font = 'bold 28px sans-serif';
      ctx.fillText('Serial Number:', L, 22);

      // Barcode — centered
      drawBarcode(ctx, job.value, 40, barcodeTop, W - 80, barcodeH);

      // Human-readable — centered
      ctx.font = '18px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(job.value, W / 2, humanY);
      ctx.textAlign = 'left';
    }

    // Copies badge
    ctx.fillStyle = '#9ca3af';
    ctx.font = '16px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`×${job.copies}`, W - 40, H - 16);
    ctx.textAlign = 'left';
  }, [job]);

  return (
    <div className="flex justify-center p-4 bg-muted rounded-lg">
      <canvas
        ref={canvasRef}
        className="border border-gray-300 rounded shadow-sm bg-white"
        style={{ width: 360, height: 195 }}
      />
    </div>
  );
}

/** Draw a simple Code 128-style barcode visualisation */
function drawBarcode(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number, y: number, maxW: number, h: number,
) {
  // Generate pseudo-random bars based on the text content
  const bars: boolean[] = [];
  // Start pattern
  bars.push(true, true, false, true, true, false, false);

  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    // Generate 6 bars per character using the char code as seed
    bars.push((code & 1) !== 0);
    bars.push((code & 2) === 0);
    bars.push((code & 4) !== 0);
    bars.push(true);
    bars.push((code & 8) === 0);
    bars.push((code & 16) !== 0);
  }
  // Stop pattern
  bars.push(true, true, false, false, true, true, true);

  const barW = Math.min(3, maxW / bars.length);
  const totalW = barW * bars.length;
  const startX = x + (maxW - totalW) / 2;

  ctx.fillStyle = '#000';
  bars.forEach((bar, i) => {
    if (bar) {
      ctx.fillRect(startX + i * barW, y, barW, h);
    }
  });
}
